/**
 * ProductTourContext - Product Tour mode (demo, no real processing)
 * - localStorage: ids_product_tour_mode = 'true'
 * - URL param: ?product_tour=true
 * - Backend: phase (1-7), completion in user table
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  isProductTourMode,
  setProductTourMode as setStorageMode,
  getProductTourPhase,
  setProductTourPhase as apiSetPhase,
  resetProductTourPhase as apiResetPhase,
  setProductTourCompleted,
} from '../services/productTourApi';

const ProductTourContext = createContext(null);

export const useProductTour = () => {
  const ctx = useContext(ProductTourContext);
  if (!ctx) {
    throw new Error('useProductTour must be used within ProductTourProvider');
  }
  return ctx;
};

export function ProductTourProvider({ children }) {
  const [active, setActive] = useState(() => isProductTourMode());
  const [phase, setPhase] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchPhase = useCallback(async () => {
    if (!isProductTourMode()) return;
    setLoading(true);
    try {
      const res = await getProductTourPhase();
      if (res?.status && res?.data?.phase != null) {
        setPhase(res.data.phase);
      }
    } catch (err) {
      console.error('Product tour fetch phase failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkUrlParam = () => {
      const search = window.location.search || '';
      const hashPart = (window.location.hash || '').split('?')[1] || '';
      const params = new URLSearchParams(search || hashPart);
      if (params.get('product_tour') === 'true') {
        setStorageMode(true);
        setActive(true);
      }
    };
    checkUrlParam();
  }, []);

  useEffect(() => {
    if (active) {
      fetchPhase();
    }
  }, [active, fetchPhase]);

  const startProductTour = useCallback(() => {
    setStorageMode(true);
    setActive(true);
    setPhase(1);
    apiResetPhase().then((res) => {
      if (res?.status && res?.data?.phase != null) {
        setPhase(res.data.phase);
      }
    });
    setProductTourCompleted(0);
  }, []);

  const endProductTour = useCallback(() => {
    setStorageMode(false);
    setActive(false);
    setProductTourCompleted(1);
  }, []);

  const advancePhase = useCallback(async (newPhase) => {
    if (!isProductTourMode()) return;
    const p = typeof newPhase === 'number' ? newPhase : phase + 1;
    const res = await apiSetPhase(Math.min(7, p));
    if (res?.status && res?.data?.phase != null) {
      setPhase(res.data.phase);
    }
  }, [phase]);

  const value = {
    isProductTour: active,
    phase,
    loading,
    startProductTour,
    endProductTour,
    advancePhase,
    fetchPhase,
  };

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
}
